/**
 * Pure helpers for "Live now" on Home (components/collab/LiveNow.jsx) over `GET /api/v1/live`: `{ projects: [{
 * projectId, name, role, owner, people, agents }] }`, people and agents in the presence shape (utils/presenceModel.js),
 * you already left out.
 */
import { activityOf, agentName, firstName, pausedLabel } from './presenceModel';

/** "Marta Rossi" → "MR", "luca" → "LU", "sara.conti@x.it" → "SC" (as vfs-service's presence). */
export function initialsOfName(name) {
  const words = String(name || '').replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) { return '?'; }
  const letters = words.length === 1 ? words[0].slice(0, 2) : `${words[0][0]}${words[words.length - 1][0]}`;
  return letters.toUpperCase();
}

const base = (path) => String(path || '').split('/').pop();

/** One project's rows, people first: `{ key, kind, mark, text, detail }`. `you`: `{ id }` to say "for you". */
export function liveRows(project, you = null) {
  const people = (project?.people || []).map((person) => ({
    key: `p:${person.id}`,
    kind: 'person',
    mark: person,
    text: `${firstName(person.name)} is ${activityOf(person).replace(/^on Home$/, 'on the project Home')}`,
    detail: person.role === 'viewer' ? 'viewer' : (person.state === 'idle' ? 'idle' : ''),
  }));
  const agents = (project?.agents || []).map((agent) => ({
    key: `a:${agent.id}`,
    kind: 'agent',
    mark: agent,
    text: agent.paused
      ? `${agentName(agent, you)} is ${pausedLabel(agent, you)}`
      : `${agentName(agent, you)} is writing ${(agent.paths || []).map(base).slice(0, 2).join(', ') || 'files'}`,
    detail: '',
  }));
  return [...people, ...agents];
}

/** The projects with someone active, and the totals: `{ projects, people, agents }`. */
export function liveSummary(answer) {
  const projects = (answer?.projects || []).filter((p) => (p.people || []).length + (p.agents || []).length > 0);
  const people = new Set();
  let agents = 0;
  projects.forEach((p) => {
    (p.people || []).forEach((person) => people.add(person.id));
    agents += (p.agents || []).length;
  });
  return { projects, people: people.size, agents };
}

/**
 * The sidebar's LIVE NOW list (under Projects): one row per live project, its name and at most `max` marks (people
 * round, agents square with their driver's initials), then how many more. `{ projectId, name, marks, more, label }`.
 */
export function sidebarLiveProjects(answer, { max = 3, you = null } = {}) {
  return liveSummary(answer).projects.map((project) => {
    const marks = [
      ...(project.people || []).map((person) => ({
        key: `p:${person.id}`, agent: false, initials: person.initials || initialsOfName(person.name), slot: person.slot,
        idle: person.state === 'idle', label: `${person.name || 'Someone'} · ${activityOf(person)}`,
      })),
      ...(project.agents || []).map((agent) => ({
        key: `a:${agent.id}`, agent: true, initials: agent.driver?.initials || 'AI', slot: agent.slot, paused: Boolean(agent.paused),
        label: agentName(agent, you),
      })),
    ];
    return {
      projectId: project.projectId,
      name: project.name || project.projectId,
      marks: marks.slice(0, max),
      more: Math.max(0, marks.length - max),
      label: `${project.name || project.projectId}: ${marks.map((mark) => mark.label).join(', ')}`,
    };
  });
}
